import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFracas } from '../contexts/FracasContext';
import { IshikawaDiagram, IshikawaCause, IshikawaCauseCategory } from '../types/rca.types';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Save, AlertCircle } from 'lucide-react';

// Refactored Components
import { IshikawaHeader } from '../components/rca/IshikawaHeader';
import { IshikawaCategoryCard } from '../components/rca/IshikawaCategoryCard';
import { IshikawaRootCauseSection } from '../components/rca/IshikawaRootCauseSection';

const defaultCategories: IshikawaCauseCategory[] = [
    'Man', 'Machine', 'Material', 'Method', 'Measurement', 'Environment'
];

export const IshikawaTool: React.FC = () => {
    const { incidentId } = useParams<{ incidentId?: string }>();
    const navigate = useNavigate();
    const { getIncidentById } = useFracas();
    const { user } = useAuth();

    const incident = incidentId ? getIncidentById(incidentId) : undefined;

    const [problemStatement, setProblemStatement] = useState(
        incident?.problemDescription || ''
    );

    const [causes, setCauses] = useState<IshikawaCause[]>(
        defaultCategories.map((category, index) => ({
            id: `cause-${index}`,
            category,
            primaryCause: '',
            secondaryCauses: [],
            isPotentialRoot: false,
            verified: false,
        }))
    );

    const [identifiedRootCauses, setIdentifiedRootCauses] = useState<string[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(causes.map(c => c.id)));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const toggleCategory = (causeId: string) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(causeId)) {
            newExpanded.delete(causeId);
        } else {
            newExpanded.add(causeId);
        }
        setExpandedCategories(newExpanded);
    };

    const updatePrimaryCause = (causeId: string, value: string) => {
        setCauses(causes.map(c =>
            c.id === causeId ? { ...c, primaryCause: value } : c
        ));
    };

    const addSecondaryCause = (causeId: string) => {
        setCauses(causes.map(c =>
            c.id === causeId
                ? { ...c, secondaryCauses: [...c.secondaryCauses, ''] }
                : c
        ));
    };

    const updateSecondaryCause = (causeId: string, index: number, value: string) => {
        setCauses(causes.map(c => {
            if (c.id === causeId) {
                const updated = [...c.secondaryCauses];
                updated[index] = value;
                return { ...c, secondaryCauses: updated };
            }
            return c;
        }));
    };

    const removeSecondaryCause = (causeId: string, index: number) => {
        setCauses(causes.map(c => {
            if (c.id === causeId) {
                return {
                    ...c,
                    secondaryCauses: c.secondaryCauses.filter((_, i) => i !== index)
                };
            }
            return c;
        }));
    };

    const togglePotentialRoot = (causeId: string) => {
        setCauses(causes.map(c =>
            c.id === causeId ? { ...c, isPotentialRoot: !c.isPotentialRoot } : c
        ));
    };

    const addRootCause = () => {
        setIdentifiedRootCauses([...identifiedRootCauses, '']);
    };

    const updateRootCause = (index: number, value: string) => {
        const updated = [...identifiedRootCauses];
        updated[index] = value;
        setIdentifiedRootCauses(updated);
    };

    const removeRootCause = (index: number) => {
        setIdentifiedRootCauses(identifiedRootCauses.filter((_, i) => i !== index));
    };

    const getCategoryColor = (category: IshikawaCauseCategory) => {
        const colors: Record<IshikawaCauseCategory, string> = {
            'Man': 'bg-blue-100 border-blue-300 text-blue-800',
            'Machine': 'bg-purple-100 border-purple-300 text-purple-800',
            'Material': 'bg-green-100 border-green-300 text-green-800',
            'Method': 'bg-orange-100 border-orange-300 text-orange-800',
            'Measurement': 'bg-pink-100 border-pink-300 text-pink-800',
            'Environment': 'bg-cyan-100 border-cyan-300 text-cyan-800',
            'Management': 'bg-indigo-100 border-indigo-300 text-indigo-800',
            'Custom': 'bg-gray-100 border-gray-300 text-gray-800',
        };
        return colors[category];
    };

    const handleSave = async () => {
        if (!user) {
            setError('Must be logged in to save analysis');
            return;
        }

        if (!problemStatement.trim()) {
            setError('Please enter a problem statement');
            return;
        }

        const hasAnyCause = causes.some(c => c.primaryCause.trim() || c.secondaryCauses.some(sc => sc.trim()));
        if (!hasAnyCause) {
            setError('Please add at least one cause');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const diagram: Omit<IshikawaDiagram, 'id'> = {
                incidentId: incidentId || undefined,
                problemStatement,
                causes: causes.filter(c => c.primaryCause.trim() || c.secondaryCauses.length > 0),
                identifiedRootCauses: identifiedRootCauses.filter(rc => rc.trim()),
                createdBy: user.uid,
                createdByName: user.displayName || user.email || 'Unknown',
                createdAt: Timestamp.fromDate(new Date()) as unknown as Date,
                updatedAt: Timestamp.fromDate(new Date()) as unknown as Date,
                organizationId: user.organizationId || '',
            };

            await addDoc(collection(db, 'ishikawa_diagrams'), diagram);

            if (incidentId) {
                navigate(`/fracas/${incidentId}`);
            } else {
                navigate('/rca');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save diagram');
            setSaving(false);
        }
    };

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <IshikawaHeader incidentId={incidentId} incident={incident} />

            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            <div id="ishikawa-diagram" className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-start gap-4">
                        <div className="flex-1">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">Problem Statement (Effect)</h2>
                            <textarea
                                value={problemStatement}
                                onChange={(e) => setProblemStatement(e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="What is the problem or effect you're analyzing?"
                            />
                        </div>
                        <div className="text-6xl">🐟</div>
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Cause Categories (6M Method)</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {causes.map((cause) => (
                            <IshikawaCategoryCard
                                key={cause.id}
                                cause={cause}
                                isExpanded={expandedCategories.has(cause.id)}
                                toggleCategory={toggleCategory}
                                updatePrimaryCause={updatePrimaryCause}
                                addSecondaryCause={addSecondaryCause}
                                updateSecondaryCause={updateSecondaryCause}
                                removeSecondaryCause={removeSecondaryCause}
                                togglePotentialRoot={togglePotentialRoot}
                                getCategoryColor={getCategoryColor}
                            />
                        ))}
                    </div>
                </div>

                <IshikawaRootCauseSection
                    identifiedRootCauses={identifiedRootCauses}
                    addRootCause={addRootCause}
                    updateRootCause={updateRootCause}
                    removeRootCause={removeRootCause}
                />

                <div className="flex items-center justify-end gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save Diagram'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default IshikawaTool;
